import React, { useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';

const toNumber = (value) => {
  if (value == null) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const normalizeLabel = (value, index, prefix = 'Point') => {
  if (value == null) return `${prefix} ${index + 1}`;
  const text = String(value).trim();
  return text.length > 0 ? text : `${prefix} ${index + 1}`;
};

const buildXYSeries = (labels, values) => {
  return labels.map((label, index) => ({
    x: label,
    y: Math.max(0, Math.round(toNumber(values?.[index]) ?? 0)),
  }));
};

const buildConfidenceBand = (dates, totalSeries, upperInput, lowerInput) => {
  const total = Array.isArray(totalSeries) ? totalSeries : [];
  const fallbackUpper = total.map((value) => Math.round((toNumber(value) ?? 0) * 1.20));
  const fallbackLower = total.map((value) => Math.round((toNumber(value) ?? 0) * 0.80));

  const upper = Array.isArray(upperInput) && upperInput.length === total.length
    ? upperInput.map((value, index) => {
        const numeric = toNumber(value);
        return numeric == null ? (fallbackUpper[index] ?? 0) : Math.round(numeric);
      })
    : fallbackUpper;

  const lower = Array.isArray(lowerInput) && lowerInput.length === total.length
    ? lowerInput.map((value, index) => {
        const numeric = toNumber(value);
        return numeric == null ? (fallbackLower[index] ?? 0) : Math.round(numeric);
      })
    : fallbackLower;

  return dates.map((date, index) => {
    const low = Math.min(lower[index] ?? 0, upper[index] ?? 0);
    const high = Math.max(lower[index] ?? 0, upper[index] ?? 0);
    return {
      x: date,
      y: [Math.max(0, low), Math.max(0, high)],
    };
  });
};

const formatTooltipUnits = (value, fallback = '-') => {
  const numeric = toNumber(value);
  if (numeric == null) return fallback;
  return `${(numeric / 1000).toLocaleString(undefined, { maximumFractionDigits: 1 })}K units`;
};

const formatTooltipBand = (start, end) => {
  const low = toNumber(start);
  const high = toNumber(end);
  if (low == null || high == null) return '-';
  return `${(low / 1000).toLocaleString(undefined, { maximumFractionDigits: 1 })}K - ${(high / 1000).toLocaleString(undefined, { maximumFractionDigits: 1 })}K units`;
};

const getTooltipLabel = (w, dataPointIndex) => {
  const categories = w?.config?.xaxis?.categories;
  const fromCategories = Array.isArray(categories) ? categories[dataPointIndex] : null;
  const fromGlobals =
    w?.globals?.categoryLabels?.[dataPointIndex] ||
    w?.globals?.categoryHeaders?.[dataPointIndex] ||
    w?.globals?.labels?.[dataPointIndex] ||
    '';
  return normalizeLabel(fromCategories || fromGlobals, dataPointIndex, 'Date');
};

/**
 * PromotionalApexChart - stacked promotional breakdown with a confidence band.
 */
const PromotionalApexChart = ({
  dates,
  baseDemand,
  promoDemand,
  confidenceUpper,
  confidenceLower,
  granularity = 'daily',
  modelingApproach = 'separated',
  region = '',
}) => {
  const chartConfig = useMemo(() => {
    if (!dates || dates.length === 0) return null;

    const safeDates = dates.map((date, index) => normalizeLabel(date, index, 'Date'));
    const baseValues = safeDates.map((_, index) => Math.max(0, Math.round(toNumber(baseDemand?.[index]) ?? 0)));
    const promoValues = safeDates.map((_, index) => Math.max(0, Math.round(toNumber(promoDemand?.[index]) ?? 0)));
    const totalValues = baseValues.map((value, index) => value + (promoValues[index] ?? 0));
    const base = buildXYSeries(safeDates, baseValues);
    const promo = buildXYSeries(safeDates, promoValues);
    const total = buildXYSeries(safeDates, totalValues);
    const confidenceUpperSeries = totalValues.map((value, index) => {
      const raw = Array.isArray(confidenceUpper) ? confidenceUpper[index] : null;
      const rawNumeric = toNumber(raw);
      return Math.max(0, rawNumeric == null ? Math.round((toNumber(value) ?? 0) * 1.12) : Math.round(rawNumeric));
    });
    const confidenceLowerSeries = totalValues.map((value, index) => {
      const raw = Array.isArray(confidenceLower) ? confidenceLower[index] : null;
      const rawNumeric = toNumber(raw);
      return Math.max(0, rawNumeric == null ? Math.round((toNumber(value) ?? 0) * 0.88) : Math.round(rawNumeric));
    });
    const confidenceBand = buildConfidenceBand(safeDates, totalValues, confidenceUpperSeries, confidenceLowerSeries);

    const normalizedApproach = String(modelingApproach || 'separated').toLowerCase();
    const isEmbedded = normalizedApproach === 'embedded';

    const baseBarSeries = buildXYSeries(safeDates, baseValues);
    const promoBarSeries = buildXYSeries(safeDates, promoValues);

    const series = isEmbedded
      ? [
          { name: 'Confidence Band', type: 'rangeArea', data: confidenceBand },
          { name: 'Combined Demand', type: 'bar', data: base },
          { name: 'Total Trend', type: 'line', data: total },
        ]
      : [
          { name: 'Base Demand', type: 'bar', data: baseBarSeries },
          { name: 'Promotional Demand', type: 'bar', data: promoBarSeries },
          { name: 'Total Demand', type: 'line', data: total },
          { name: 'Confidence Band', type: 'rangeArea', data: confidenceBand },
        ];

    const colors = isEmbedded
      ? ['#0057B8', '#10B981', '#10B981']
      : ['#0057B8', '#8B5CF6', '#10B981', '#10B981'];

    const xLabelFormatter = (val) => {
      if (!val) return '';
      const text = String(val);
      if (granularity === 'monthly') return text;
      if (granularity === 'weekly') return text.slice(5);
      return text.length > 7 ? text.slice(5) : text;
    };

    const options = {
      chart: {
        id: 'promotional-forecast-chart',
        type: 'bar',
        stacked: !isEmbedded,
        stackOnlyBar: true,
        stackType: 'normal',
        height: 440,
        toolbar: {
          show: true,
          autoSelected: 'zoom',
          tools: {
            download: true,
            selection: false,
            zoom: true,
            zoomin: true,
            zoomout: true,
            pan: false,
            reset: true,
          },
          export: {
            png: { filename: `promotional-forecast-${region}-${granularity}` },
            csv: { filename: `promotional-forecast-${region}-${granularity}` },
          },
        },
        zoom: { enabled: true },
        animations: {
          enabled: true,
          easing: 'easeinout',
          speed: 600,
          animateGradually: { enabled: true, delay: 80 },
        },
        fontFamily: 'Inter, -apple-system, sans-serif',
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: dates.length > 60 ? '82%' : dates.length > 20 ? '58%' : '52%',
          borderRadius: isEmbedded ? 4 : 3,
          borderRadiusApplication: 'end',
          borderRadiusWhenStacked: 'last',
        },
      },
      colors,
      stroke: {
        width: isEmbedded ? [0, 2.5, 0] : [0, 0, 2.5, 0],
        curve: 'smooth',
        dashArray: isEmbedded ? [0, 0, 0] : [0, 0, 0, 6],
      },
      fill: {
        type: isEmbedded ? ['solid', 'solid', 'solid'] : ['solid', 'solid', 'solid', 'solid'],
        opacity: isEmbedded ? [0.9, 1, 0.20] : [0.9, 1, 1, 0.22],
      },
      states: {
        hover: {
          filter: { type: 'none' },
        },
      },
      markers: {
        size: isEmbedded ? [0, 3, 0] : [0, 0, 3, 0],
        colors: ['#F59E0B'],
        strokeColors: '#fff',
        strokeWidth: 2,
        hover: { size: 5 },
      },
      xaxis: {
        type: 'category',
        categories: safeDates,
        labels: {
          rotate: granularity === 'daily' && safeDates.length > 20 ? -35 : 0,
          style: { fontSize: '11px', fontFamily: 'Inter, sans-serif', colors: '#64748B' },
          formatter: (val) => xLabelFormatter(val),
        },
        axisBorder: { show: false },
        axisTicks: { show: false },
        tickAmount: Math.min(safeDates.length, granularity === 'monthly' ? 12 : 15),
      },
      yaxis: {
        title: {
          text: granularity === 'daily' ? 'Demand Units/Day' : granularity === 'weekly' ? 'Demand Units/Week' : 'Demand Units/Month',
          style: { fontSize: '12px', fontWeight: 600, color: '#64748B' },
        },
        labels: {
          style: { fontSize: '11px', colors: '#64748B' },
          formatter: (val) => (
            val >= 1_000_000
              ? `${(val / 1_000_000).toFixed(1)}M`
              : val >= 1_000
                ? `${(val / 1_000).toFixed(1)}K`
                : Math.round(val).toLocaleString()
          ),
        },
      },
      tooltip: {
        shared: true,
        intersect: false,
        theme: 'light',
        style: { fontSize: '12px', fontFamily: 'Inter, sans-serif' },
        custom: ({ dataPointIndex, w }) => {
          const safeIndex = Number.isInteger(dataPointIndex) && dataPointIndex >= 0 ? dataPointIndex : 0;
          const label = getTooltipLabel(w, safeIndex);
          const collapsed = new Set(w?.globals?.collapsedSeriesIndices || []);
          let html = `<div class="apexcharts-tooltip-title" style="font-family: Inter, sans-serif; font-size: 12px; font-weight: 600; padding: 6px 10px; background: #F8FAFC; border-bottom: 1px solid #E2E8F0; color: #475569;">${label}</div>`;

          const rows = isEmbedded
            ? [
                { name: 'Confidence Band', value: formatTooltipBand(confidenceLowerSeries[safeIndex], confidenceUpperSeries[safeIndex]) },
                { name: 'Combined Demand', value: formatTooltipUnits(baseValues[safeIndex]) },
                { name: 'Total Trend', value: formatTooltipUnits(totalValues[safeIndex]) },
              ]
            : [
                { name: 'Confidence Band', value: formatTooltipBand(confidenceLowerSeries[safeIndex], confidenceUpperSeries[safeIndex]) },
                { name: 'Base Demand', value: formatTooltipUnits(baseValues[safeIndex]) },
                { name: 'Promotional Demand', value: formatTooltipUnits(promoValues[safeIndex]) },
                { name: 'Total Demand', value: formatTooltipUnits(totalValues[safeIndex]) },
              ];

          rows.forEach((row, idx) => {
            if (collapsed.has(idx)) return;

            const colorByName = {
              'Confidence Band': '#34D399',
              'Base Demand': '#0057B8',
              'Promotional Demand': '#8B5CF6',
              'Total Demand': '#10B981',
              'Combined Demand': '#0057B8',
              'Total Trend': '#10B981',
            };
            const color = colorByName[row.name] || w?.globals?.colors?.[idx] || '#64748B';
            html += `
              <div class="apexcharts-tooltip-series-group apexcharts-active" style="display: flex; align-items: center; padding: 6px 10px; gap: 8px;">
                <span class="apexcharts-tooltip-marker" style="background-color: ${color}; width: 8px; height: 8px; border-radius: 50%; display: inline-block; flex-shrink: 0;"></span>
                <div class="apexcharts-tooltip-text" style="font-family: Inter, sans-serif; font-size: 12px; display: flex; align-items: center; gap: 4px; width: 100%;">
                  <span class="apexcharts-tooltip-text-y-label" style="color: #64748B;">${row.name}:</span>
                  <span class="apexcharts-tooltip-text-y-value" style="font-weight: 600; color: #1E293B;">${row.value}</span>
                </div>
              </div>
            `;
          });

          return html;
        }
      },
      legend: {
        show: true,
        position: 'top',
        horizontalAlign: 'center',
        offsetY: 4,
        fontSize: '13px',
        fontFamily: 'Inter, sans-serif',
        markers: { width: 10, height: 10, radius: 999 },
        onItemClick: { toggleDataSeries: true },
      },
      annotations: { xaxis: [] },
      grid: {
        borderColor: '#E2E8F0',
        strokeDashArray: 4,
        yaxis: { lines: { show: true } },
        xaxis: { lines: { show: false } },
        padding: { top: 0, right: 12, bottom: 0, left: 8 },
      },
      dataLabels: { enabled: false },
      responsive: [
        {
          breakpoint: 768,
          options: {
            chart: { height: 300 },
            legend: { position: 'bottom' },
          },
        },
      ],
    };

    return { series, options };
  }, [dates, baseDemand, promoDemand, confidenceUpper, confidenceLower, granularity, modelingApproach, region]);

  if (!chartConfig) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem', color: '#94A3B8' }}>
        <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>Chart</div>
        <p style={{ fontWeight: 600 }}>No promotional forecast data</p>
        <p style={{ fontSize: '0.875rem' }}>Run a forecast to see the promotional analysis chart</p>
      </div>
    );
  }

  return (
    <div className="apex-chart-wrapper">
      <ReactApexChart
        options={chartConfig.options}
        series={chartConfig.series}
        type="bar"
        height={420}
      />
    </div>
  );
};

export default PromotionalApexChart;
